
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Star,
  Download,
  Check,
  BarChart,
  MessageSquare,
  User,
  Calendar,
  Share2
} from "lucide-react";

// Mock workflow data - in a real app this would come from an API
const mockWorkflow = {
  id: "wf1",
  title: "Customer Onboarding Automation",
  description: "Automate your customer onboarding process with this comprehensive Zapier workflow that connects your CRM, email marketing platform, and task management system.",
  price: 29.99,
  rating: 4.8,
  reviewCount: 124,
  platform: "Zapier",
  category: "Sales",
  image: "https://images.unsplash.com/photo-1518893494013-481c1d8ed3fd?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
  seller: {
    name: "Automation Pro",
    avatar: "AP",
    bio: "Automation expert with over 10 years experience. Specialized in sales and marketing workflows.",
    totalSales: 1243,
    rating: 4.9
  },
  features: [
    "Automatic lead scoring based on custom criteria",
    "Personalized welcome email sequences",
    "Task creation and assignment to sales team",
    "Data syncing between CRM and marketing platforms",
    "Follow-up reminders and notifications"
  ],
  requirements: [
    "Zapier account (Premium recommended)",
    "Supported CRM: Hubspot, Salesforce, or Pipedrive",
    "Supported email platforms: Mailchimp, ConvertKit, or ActiveCampaign"
  ],
  reviews: [
    {
      id: "rev1",
      user: "Samantha K.",
      rating: 5,
      date: "2023-10-15",
      comment: "This workflow saved our sales team hours every week. Setup was straightforward and the documentation was excellent."
    },
    {
      id: "rev2",
      user: "Mark T.",
      rating: 4,
      date: "2023-09-22",
      comment: "Great workflow that needed just a bit of customization for our specific needs. Support from the seller was very helpful."
    },
    {
      id: "rev3",
      user: "Jamie L.",
      rating: 5,
      date: "2023-08-10",
      comment: "Impressive automation that streamlined our entire customer onboarding. Worth every penny!"
    }
  ]
};

const WorkflowDetail = () => {
  const { id } = useParams();
  const [workflow, setWorkflow] = useState(mockWorkflow);
  
  useEffect(() => {
    // In a real app, fetch the workflow data using the ID
    console.log(`Fetching workflow with ID: ${id}`);
    
    // Update page title
    document.title = `${mockWorkflow.title} - FlowMarket`;
    
    // For now, we'll just use our mock data
    setWorkflow(mockWorkflow);
    
    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, [id]);
  
  if (!workflow) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow container mx-auto px-4 md:px-6 py-16 flex items-center justify-center">
          <p>Loading workflow details...</p>
        </main>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow container mx-auto px-4 md:px-6 py-8">
        <div className="flex items-center gap-2 mb-8">
          <Link to="/marketplace" className="text-muted-foreground hover:text-primary">
            Marketplace
          </Link>
          <span className="text-muted-foreground">/</span>
          <Link to={`/marketplace?category=${workflow.platform.toLowerCase()}`} className="text-muted-foreground hover:text-primary">
            {workflow.platform}
          </Link>
          <span className="text-muted-foreground">/</span>
          <span className="text-foreground">{workflow.title}</span>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Workflow Details */}
          <div className="lg:col-span-2">
            <div className="mb-8">
              <div className="flex flex-wrap gap-2 mb-2">
                <Badge variant="secondary" className="font-normal">{workflow.platform}</Badge>
                <Badge variant="outline" className="font-normal">{workflow.category}</Badge>
              </div>
              
              <h1 className="text-3xl font-bold mb-3">{workflow.title}</h1>
              
              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center gap-1.5">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-4 h-4 ${i < Math.floor(workflow.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
                      />
                    ))}
                  </div>
                  <span className="text-sm">{workflow.rating} ({workflow.reviewCount} reviews)</span>
                </div>
                
                <div className="text-sm text-muted-foreground flex items-center gap-1">
                  <Download className="h-4 w-4" />
                  <span>{workflow.seller.totalSales} sales</span>
                </div>
              </div>
            </div>
            
            {/* Workflow Image */}
            <div className="rounded-lg overflow-hidden mb-8 border border-border">
              <img 
                src={workflow.image} 
                alt={workflow.title} 
                className="w-full h-auto object-cover" 
              />
            </div>
            
            <Tabs defaultValue="description" className="mb-8">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="description">Description</TabsTrigger>
                <TabsTrigger value="features">Features</TabsTrigger>
                <TabsTrigger value="reviews">Reviews</TabsTrigger>
              </TabsList>
              
              <TabsContent value="description" className="py-6">
                <div className="prose max-w-none">
                  <p className="text-foreground mb-6">{workflow.description}</p>
                  
                  <h3 className="text-xl font-semibold mb-3">Requirements</h3>
                  <ul className="space-y-2 mb-6">
                    {workflow.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <h3 className="text-xl font-semibold mb-3">About the Seller</h3>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary font-medium">
                      {workflow.seller.avatar}
                    </div>
                    <div>
                      <h4 className="font-medium">{workflow.seller.name}</h4>
                      <div className="flex items-center gap-1 text-sm">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`w-3 h-3 ${i < Math.floor(workflow.seller.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
                            />
                          ))}
                        </div>
                        <span>{workflow.seller.rating}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{workflow.seller.bio}</p>
                </div>
              </TabsContent>
              
              <TabsContent value="features" className="py-6">
                <h3 className="text-xl font-semibold mb-4">What's Included</h3>
                <ul className="space-y-3">
                  {workflow.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </TabsContent>
              
              <TabsContent value="reviews" className="py-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold">Customer Reviews</h3>
                  <Button variant="outline">Write a Review</Button>
                </div>
                
                <div className="space-y-6">
                  {workflow.reviews.map((review) => (
                    <div key={review.id} className="pb-6 border-b border-border">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                            <User className="h-4 w-4" />
                          </div>
                          <span className="font-medium">{review.user}</span>
                        </div>
                        <div className="flex items-center gap-1 text-sm">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                        <Calendar className="h-3 w-3" />
                        <span>{review.date}</span>
                      </div>
                      <p className="text-foreground">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
          
          {/* Right Column: Purchase Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 border border-border rounded-lg p-6 bg-card">
              <div className="text-3xl font-bold mb-4">${workflow.price}</div>
              
              <Button className="w-full mb-3">
                Buy Now
              </Button>
              
              <Button variant="outline" className="w-full mb-6">
                Add to Cart
              </Button>
              
              <div className="text-sm text-muted-foreground mb-4">
                <div className="flex justify-between py-3 border-b border-border">
                  <span>Platform</span>
                  <span className="font-medium text-foreground">{workflow.platform}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <span>Category</span>
                  <span className="font-medium text-foreground">{workflow.category}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <span>Last Updated</span>
                  <span className="font-medium text-foreground">October 2023</span>
                </div>
              </div>
              
              <div className="flex flex-col gap-3 mb-6">
                <div className="flex items-center gap-2">
                  <BarChart className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm">Usage analytics included</span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm">30-day support from seller</span>
                </div>
                <div className="flex items-center gap-2">
                  <Download className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm">Instant download after purchase</span>
                </div>
              </div>
              
              <Button variant="ghost" className="w-full flex items-center gap-2">
                <Share2 className="h-4 w-4" />
                <span>Share</span>
              </Button>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default WorkflowDetail;
